import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Typography, Stepper, Step, StepButton, StepLabel, Paper, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useBook } from '../../Context/BookContext';
import UploadStage from '../ProcessStages/UploadStage';
import StylingStage from '../ProcessStages/StylingStage';
import GenerateImagesStage from '../ProcessStages/GenerateImagesStage';
import DownloadReadStage from '../ProcessStages/DownloadReadStage';

const steps = [
  { id: 'upload', label: 'Upload' },
  { id: 'styling', label: 'Styling' },
  { id: 'generate-images', label: 'Generate Images' },
  { id: 'download-read', label: 'Download / Read' }
];

const ProcessBookPage: React.FC = () => {
  const navigate = useNavigate();
  const { stage } = useParams<{ stage?: string }>();
  const location = useLocation();
  const { selectedBook } = useBook();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Determine active step based on URL
  const currentStageId = stage || 'upload';
  const activeStep = steps.findIndex(step => step.id === currentStageId);

  // Determine which steps are unlocked
  const isStepUnlocked = (stepIndex: number): boolean => {
    // Stage 0 (Upload) - always unlocked
    if (stepIndex === 0) return true;
    
    // Stage 3 (Download/Read) - unlocked if book exists
    if (stepIndex === 3) return selectedBook !== null;
    
    // Stages 1 & 2 (Styling, Generate Images) - unlocked if book is completed
    if (stepIndex === 1 || stepIndex === 2) {
      return selectedBook?.ProcessingStatus === 'completed';
    }
    
    return false;
  };

  const handleStepClick = (stepIndex: number) => {
    if (isStepUnlocked(stepIndex)) {
      const stepId = steps[stepIndex].id;
      navigate(`/process-book/${stepId}`);
    }
  };

  // Redirect to upload if no stage specified
  useEffect(() => {
    if (!stage) {
      navigate('/process-book/upload', { replace: true });
    }
  }, [stage, navigate]);

  // Memoize navigation callbacks to prevent component remounting
  const handleUploadNext = useCallback(() => {
    navigate('/process-book/styling');
  }, [navigate]);

  const handleStylingNext = useCallback(() => {
    navigate('/process-book/generate-images');
  }, [navigate]);

  const handleGenerateImagesNext = useCallback(() => {
    navigate('/process-book/download-read');
  }, [navigate]);

  // Memoize stage content to prevent remounting when selectedBook updates
  const stageContent = useMemo(() => {
    switch (currentStageId) {
      case 'upload':
        return <UploadStage onNext={handleUploadNext} />;
      case 'styling':
        return <StylingStage onNext={handleStylingNext} />;
      case 'generate-images':
        return <GenerateImagesStage onNext={handleGenerateImagesNext} />;
      case 'download-read':
        return <DownloadReadStage />;
      default:
        return <UploadStage onNext={handleUploadNext} />;
    }
  }, [currentStageId, handleUploadNext, handleStylingNext, handleGenerateImagesNext]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: 'text.primary', mb: 1, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
          Process Book
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: { xs: '0.9rem', md: '1rem' } }}>
          {selectedBook 
            ? `Processing: ${selectedBook.Title || selectedBook.FileName}` 
            : 'Upload and process your book through each stage'
          }
        </Typography>
      </Box>

      {/* Stepper */}
      <Paper sx={{ 
        p: { xs: 2, md: 3 }, 
        mb: 4, 
        backgroundColor: 'background.paper',
        overflow: 'auto' 
      }}>
        <Stepper 
          activeStep={activeStep} 
          nonLinear
          orientation={isMobile ? 'vertical' : 'horizontal'}
          sx={{
            '& .MuiStepLabel-label': {
              fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' }
            },
            '& .MuiStepConnector-line': {
              minWidth: { xs: '20px', sm: '40px' }
            }
          }}
        >
          {steps.map((step, index) => {
            const unlocked = isStepUnlocked(index);
            return (
              <Step key={step.id} completed={false}>
                <StepButton
                  onClick={() => handleStepClick(index)}
                  disabled={!unlocked}
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: { xs: '0.875rem', sm: '0.875rem', md: '1rem' },
                      fontWeight: activeStep === index ? 700 : 500
                    }
                  }}
                >
                  <StepLabel
                    sx={{
                      '& .MuiStepIcon-root': {
                        fontSize: { xs: '1.75rem', sm: '1.75rem', md: '2rem' },
                        '&.Mui-active': {
                          color: 'primary.main'
                        },
                        '&.Mui-disabled': {
                          color: 'action.disabled'
                        }
                      }
                    }}
                  >
                    {step.label}
                  </StepLabel>
                </StepButton>
              </Step>
            );
          })}
        </Stepper>
      </Paper>

      {/* Stage Content */}
      <Box>
        {stageContent}
      </Box>
    </Box>
  );
};

export default ProcessBookPage;

